import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { NO_ACTIVE_CONTRACT } from './constants/auth';
import { AuthFlow } from './lib/AuthFlow';
import getCurrentServices from './lib/getCurrentServices';
import getCurrentUser from './lib/getCurrentUser';
import { AuthUser } from './lib/types';
import { authState } from './state';
import _ from 'lodash';

class AuthModule extends AppModule {
  private authFlow: AuthFlow;
  override events = ['auth.signin', 'auth.signout', 'auth.changed'];
  private refreshUserInterval: NodeJS.Timeout | undefined;
  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    // create the AuthFlow helper class
    const { AUTH_IUM_ISSUER_URL, AUTH_IUM_CLIENT_ID } = environment;
    this.authFlow = new AuthFlow(
      AUTH_IUM_ISSUER_URL,
      AUTH_IUM_CLIENT_ID,
      [
        'openid',
        'st.account_service.all', // needed for calling account-service
        'email', // needed for calling monolith web apis
        'st.multi_tenant_user_apis', // needed for calling monolith web apis
      ],
      '/signin-oidc',
      '/signout-callback-oidc',
      this.app.logger,
      async (authenticated: boolean) => {
        if (authenticated) {
          this.setAuthenticated();
        }
      },
    );
  }

  override preload(): void {
    this.app.events.on('app.started', () => {
      if (this.bypassIUM) {
        this.setAuthenticated();
        return;
      }
      if (this.authFlow.signedIn()) {
        this.setAuthenticated();
      }
    });
  }

  public async signin(idp?: string): Promise<void> {
    authState.loading = true;
    authState.loadingState = 'signin';
    authState.error = undefined;
    if (this.bypassIUM) {
      this.setAuthenticated();
      return;
    }
    if (!this.authFlow.signedIn()) {
      await this.authFlow.fetchServiceConfiguration();
      return this.authFlow.makeAuthorizationRequest(undefined, idp);
    }
    this.setAuthenticated();
  }

  public async completeSignin(): Promise<void> {
    await this.authFlow.fetchServiceConfiguration();
    return this.authFlow.completeAuthorization();
  }

  public get signedIn(): boolean {
    return authState.signedIn;
  }

  public async signout(): Promise<void> {
    authState.loading = true;
    authState.loadingState = 'signout';
    authState.error = undefined;
    this.setNotAuthenticated();
    this.authFlow.makeEndSessionRequest();
  }

  public get user(): AuthUser | undefined {
    return authState.user;
  }

  public async getCurrentToken(): Promise<string> {
    if (this.bypassIUM) {
      return this.bypassIUMId;
    }
    if (!this.authFlow.isTokenValid(-100)) {
      await this.authFlow.fetchServiceConfiguration();
      await this.authFlow.refreshTokens();
      authState.token = this.authFlow.accessToken!;
    }

    return this.authFlow.accessToken!;
  }

  public getCurrentServiceId(): string {
    return authState.serviceId || '';
  }
  private async refreshUser() {
    if (!authState.user) {
      this.app.logger.error('No user to refresh');
      return;
    }
    try {
      const refreshedUser = await getCurrentUser(true);
      if (refreshedUser.seatLevel !== authState.user?.seatLevel) {
        authState.user.seatLevel = refreshedUser.seatLevel;
        this.app.events.emit('auth.changed');
      }
      if (
        !_.isEqual(refreshedUser.personalLine, authState.user?.personalLine)
      ) {
        authState.user.personalLine = refreshedUser.personalLine;
        this.app.events.emit('auth.changed');
      }
      if (!_.isEqual(refreshedUser.permissions, authState.user?.permissions)) {
        authState.user.permissions = refreshedUser.permissions;
        this.app.events.emit('auth.changed');
      }
      if (
        refreshedUser.outboundCallsAllowed !==
        authState.user?.outboundCallsAllowed
      ) {
        authState.user.outboundCallsAllowed =
          refreshedUser.outboundCallsAllowed;
        this.app.events.emit('auth.changed');
      }
    } catch (err) {
      this.app.logger.error('Error refreshing user', err);
    }
  }

  private async setAuthenticated(): Promise<void> {
    if (authState.signedIn) {
      return;
    }

    authState.loading = true;
    authState.loadingState = 'signin';
    try {
      authState.token = this.bypassIUM
        ? this.bypassIUMId
        : this.authFlow.accessToken!;
      if (!authState.serviceId) {
        // select default service
        const services = await getCurrentServices();
        if (services.length === 0) {
          throw new Error('No services found');
        }
        authState.serviceId = services[0].id;
        authState.serviceName = services[0].name;
      }
      authState.user = await getCurrentUser();
      if (!authState.user.org.hasActiveContract) {
        throw new Error(NO_ACTIVE_CONTRACT);
      }
      authState.signedIn = true;
      this.app.events.emit('auth.signin', this.user);
      this.refreshUserInterval = setInterval(
        this.refreshUser.bind(this),
        60000,
      );
    } catch (err) {
      this.app.logger.error('Error setting auth', err);
      this.authFlow.clear();
      authState.error = extractErrorMessage(err);
      authState.serviceId = '';
      authState.serviceName = '';
      authState.token = '';
      authState.user = undefined;
      authState.signedIn = false;
    } finally {
      authState.loading = false;
      authState.loadingState = 'none';
    }
  }

  private setNotAuthenticated(): void {
    if (!authState.signedIn) {
      return;
    }

    authState.serviceId = '';
    authState.serviceName = '';
    authState.token = '';
    authState.user = undefined;
    authState.signedIn = false;
    clearInterval(this.refreshUserInterval);
    this.app.events.emit('auth.signout');
  }

  private get bypassIUM(): boolean {
    return environment.AUTH_BYPASS_IUM;
  }

  private get bypassIUMId(): string {
    return `bypass-${environment.AUTH_BYPASS_IUM_ID}`;
  }
}

export default new AuthModule(appManager.events, appManager.logger('Auth'));
